import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { TFSLoader, ErrorMessage } from '../components/ui';
import { RefreshButton, Pagination } from '../components/common';
import { RequestListPageHeader, RequestListTable } from '../components/features';
import { useRequests } from '../contexts/RequestsContext';

const RequestList = () => {
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterType, setDateFilterType] = useState('single'); // 'single' or 'range'
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  
  // Use shared requests context
  const {
    requests,
    pagination: contextPagination,
    loading: requestsLoading,
    isRefreshing,
    lastRefreshTime,
    error,
    fetchRequests,
    updateRequest,
    clearError
  } = useRequests();

  useEffect(() => {
    if (contextPagination) {
      setPagination(contextPagination);
    }
  }, [contextPagination]);

  useEffect(() => {
    const safeRequests = Array.isArray(requests) ? requests : [];
    setFilteredRequests(safeRequests);
  }, [requests]);

  useEffect(() => {
    setIsSearching(true);
    const timer = setTimeout(() => {
      const filters = {
        search: searchTerm,
        status: statusFilter,
        sort_by: sortConfig.key,
        sort_order: sortConfig.direction
      };
      
      // Add date filters
      if (dateFilterType === 'single' && dateFilter) {
        filters.date = dateFilter;
      } else if (dateFilterType === 'range') {
        if (startDate) filters.start_date = startDate;
        if (endDate) filters.end_date = endDate;
      }
      
      fetchRequests(false, currentPage, 10, filters).finally(() => {
        setIsSearching(false);
      });
    }, 500);
    
    return () => {
      clearTimeout(timer);
      setIsSearching(false);
    };
  }, [searchTerm, statusFilter, dateFilter, startDate, endDate, dateFilterType, sortConfig, currentPage, fetchRequests]);

  const handleRefresh = () => {
    const filters = {
      search: searchTerm,
      status: statusFilter,
      sort_by: sortConfig.key,
      sort_order: sortConfig.direction
    };
    
    // Add date filters
    if (dateFilterType === 'single' && dateFilter) {
      filters.date = dateFilter;
    } else if (dateFilterType === 'range') {
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
    }
    
    fetchRequests(true, currentPage, 10, filters);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    const success = await updateRequest(requestId, { front_status: newStatus });
    if (!success) {
      // Error is already handled in the context
    }
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterTypeChange = (e) => {
    setDateFilterType(e.target.value);
    // Clear date filters when switching types
    setDateFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const clearDateFilters = () => {
    setDateFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  if (requestsLoading && !isSearching) {
    return <TFSLoader />;
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-fadeIn p-4 md:p-0">
      {/* Error Message */}
      <ErrorMessage
        error={error}
        onDismiss={clearError}
      />

      {/* Header */}
      <RequestListPageHeader
        title="Freight Requests"
        description="Manage and track all international transport requests"
        lastUpdated={lastRefreshTime}
      >
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Total:</span>
          <span className="text-lg font-semibold text-gray-900">{pagination?.total_items || 0}</span>
        </div>
        <RefreshButton
          onClick={handleRefresh}
          isRefreshing={isRefreshing}
          loading={requestsLoading}
        />
      </RequestListPageHeader>

      {/* Modern SaaS Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Filter Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Filters</h3>
            {(searchTerm || statusFilter !== 'all' || dateFilter || startDate || endDate) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  clearDateFilters();
                }}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Filter Content */}
        <div className="p-6">
          {/* Top Row - Search and Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Enhanced Search */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Search Requests</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Search by ID, company, or content..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Enhanced Status Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={handleStatusChange}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm appearance-none cursor-pointer"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Date Filter Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Date Filter</label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDateFilterTypeChange({ target: { value: 'single' } })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                    dateFilterType === 'single'
                      ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  Single Date
                </button>
                <button
                  onClick={() => handleDateFilterTypeChange({ target: { value: 'range' } })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                    dateFilterType === 'range'
                      ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  Date Range
                </button>
              </div>
            </div>

            {/* Date Inputs with Modern Design */}
            {dateFilterType === 'single' ? (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  className={`block w-full pl-10 ${dateFilter ? 'pr-10' : 'pr-4'} py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm`}
                />
                {dateFilter && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      onClick={() => setDateFilter('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                      type="button"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">From Date</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CalendarIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      className={`block w-full pl-9 ${startDate ? 'pr-10' : 'pr-4'} py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm`}
                    />
                    {startDate && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <button
                          onClick={() => setStartDate('')}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                          type="button"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">To Date</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CalendarIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      className={`block w-full pl-9 ${endDate ? 'pr-10' : 'pr-4'} py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm`}
                    />
                    {endDate && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <button
                          onClick={() => setEndDate('')}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                          type="button"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Active Filters Display */}
            {(searchTerm || statusFilter !== 'all' || dateFilter || startDate || endDate) && (
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Filters</span>
                  <span className="text-xs text-gray-400">
                    {pagination?.total_items || 0} results
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {searchTerm && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      Search: "{searchTerm}"
                      <button
                        onClick={() => setSearchTerm('')}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {statusFilter !== 'all' && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      Status: {statusFilter}
                      <button
                        onClick={() => setStatusFilter('all')}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {dateFilter && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                      Date: {dateFilter}
                      <button
                        onClick={() => setDateFilter('')}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {(startDate || endDate) && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                      Range: {startDate || '...'} to {endDate || '...'}
                      <button
                        onClick={clearDateFilters}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Table */}
      <div className="relative">
        {isSearching && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
            <TFSLoader />
          </div>
        )}
        <RequestListTable 
          currentRequests={filteredRequests}
          requests={requests}
          sortConfig={sortConfig}
          handleSort={handleSort}
          handleUpdateStatus={handleUpdateStatus}
          isRefreshing={isRefreshing}
          requestsLoading={requestsLoading}
          handleRefresh={handleRefresh}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          navigate={navigate}
        />
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <Pagination 
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          setCurrentPage={handlePageChange}
          nextPage={() => pagination.has_next && handlePageChange(pagination.page + 1)}
          prevPage={() => pagination.has_previous && handlePageChange(pagination.page - 1)}
          hasNextPage={pagination.has_next}
          hasPrevPage={pagination.has_previous}
          startIndex={(pagination.page - 1) * pagination.page_size}
          endIndex={Math.min(pagination.page * pagination.page_size, pagination.total_items)}
          totalItems={pagination.total_items}
        />
      )}  
      
    </div>
  );
};

export default RequestList;